import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CreateEventSkeletonLoader from './CreateEventSkeletonLoader';
import CustomModal from './CustomModal';
import LevelAndUserButton from './LevelAndUserButton';

// This component allows users to create a new Crazy Cup event by selecting courses, entering an event name, and setting a date.
function CreateNewCup() {
  const [cupName, setCupName] = useState('');
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [golfCourses, setGolfCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [totalPar, setTotalPar] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fadeIn, setFadeIn] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [newCourseName, setNewCourseName] = useState('');
  const [newCourseHoles, setNewCourseHoles] = useState(Array(18).fill({ hole_number: 0, par: 0 }));
  const [newCourseTotalPar, setNewCourseTotalPar] = useState(0);
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', message: '', onConfirm: null });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
  if (showModal) {
    setNewCourseName('');
    setNewCourseHoles(Array(18).fill({ hole_number: 0, par: 0 }));
    setNewCourseTotalPar(0);
  }
}, [showModal]);

  // Fetch golf courses from the server when the component mounts
  useEffect(() => {
    const startTime = Date.now();
    setLoading(true);

    fetch('/server/crazy-cup-server/getCourseNames.php')
      .then((response) => response.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setGolfCourses(data);
        } else {
          console.error('Invalid data format:', data);
        }
      })
      .catch((error) => console.error('Error fetching golf courses:', error))
      .finally(() => {
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(200 - elapsedTime, 0);
        setTimeout(() => {
          setLoading(false);
        }, remainingTime);
      });
  }, []);

  // this effect runs when selectedCourses changes and calculates the total par for the selected courses
  useEffect(() => {
    const total = selectedCourses.reduce((sum, course) => sum + Number(course.par), 0);
    setTotalPar(total);
  }, [selectedCourses]);

  // this function validates the inputs before creating a new event
  const validateInputs = () => {
  if (!cupName.trim()) {
    setModalContent({
      title: 'CANNOT CREATE EVENT',
      message: 'Event name cannot be empty.',
      onConfirm: () => setModalOpen(false),
    });
    setModalOpen(true);
    return false;
  }

  // this validation checks if the selected date is today or in the future
  if (!selectedDate) {
    setModalContent({
      title: 'Validation Error',
      message: 'Please select a valid future date.',
      onConfirm: () => setModalOpen(false),
    });
    setModalOpen(true);
    return false;
  }
  const selected = new Date(selectedDate);
  const now = new Date();
  // Set time to midnight for both dates to compare only the date part
  selected.setHours(0,0,0,0);
  now.setHours(0,0,0,0);
  if (selected < now) {
    setModalContent({
      title: 'Validation Error',
      message: 'Please select a valid future date.',
      onConfirm: () => setModalOpen(false),
    });
    setModalOpen(true);
    return false;
  }

  // this validation checks if at least one course is selected
  if (selectedCourses.length === 0) {
    setModalContent({
      title: 'PICK A COURSE!',
      message: 'Please select at least one course.',
      onConfirm: () => setModalOpen(false),
    });
    setModalOpen(true);
    return false;
  }

  return true;
};

  // this function handles adding a selected course to the list of selected courses
  const handleAddCourse = () => {
    if (selectedCourse && selectedCourse.name && selectedCourse.par) {
      setSelectedCourses([...selectedCourses, selectedCourse]);
    } else {
      console.error('Invalid selected course:', selectedCourse);
    }
  };

  const handleRemoveCourse = (courseName) => {
    setSelectedCourses(selectedCourses.filter((course) => course.name !== courseName));
  };

  const escapeInput = (input) => {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const handleCreateCup = () => {
  if (!validateInputs()) return;

  const escapedCupName = escapeInput(cupName);

  const newEvent = {
    cupName: escapedCupName,
    date: selectedDate,
    totalPar,
    courses: selectedCourses,
  };

  // Send sanitized and escaped data to the server
  fetch('/server/crazy-cup-server/addEvent.php', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(newEvent),
  })
    .then((response) => response.json())
    .then((data) => {
      if (data.success) {
        setModalContent({
          title: 'Success',
          message: 'Event created successfully!',
          onConfirm: () => navigate('/current-events'),
        });
        setModalOpen(true);
      } else {
        setModalContent({
          title: 'Error',
          message: `Error creating event: ${data.message}`,
          onConfirm: () => setModalOpen(false),
        });
        setModalOpen(true);
      }
    })
    .catch((error) => {
      setModalContent({
        title: 'Error',
        message: 'An unexpected error occurred. Please try again later.',
        onConfirm: () => setModalOpen(false),
      });
      setModalOpen(true);
      console.error('Error:', error);
    });
};

// this function handles adding a new course to the list of golf courses
const handleAddNewCourse = () => {
  const allHolesHavePar = newCourseHoles.every(
    (hole) => hole.par && Number(hole.par) > 0
  );

  if (!allHolesHavePar) {
    setModalContent({
      title: 'Missing Par Values',
      message: 'Please enter a valid par (1-5) for every hole before adding the course.',
      onConfirm: () => setModalOpen(false),
    });
    setModalOpen(true);
    return;
  }

  if (!newCourseName.trim()) {
    setModalContent({
      title: 'Missing Course Name',
      message: 'Please enter a name for the course before adding it.',
      onConfirm: () => setModalOpen(false),
    });
    setModalOpen(true);
    return;
  }

  const newCourse = {
    name: newCourseName,
    par: newCourseTotalPar,
    holes: newCourseHoles.map((hole, index) => ({
      hole_number: index + 1,
      par: hole.par,
    })),
  };

  // Send the new course data to the server
  fetch('/server/crazy-cup-server/addCourse.php', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(newCourse),
  })
    .then((response) => response.json())
    .then((data) => {
      if (data.success) {
        setModalContent({
          title: 'Course Added',
          message: 'Course added successfully!',
          onConfirm: () => {
            setModalOpen(false);
            setGolfCourses([...golfCourses, { name: newCourseName, par: newCourseTotalPar }]);
            setShowModal(false);
          },
        });
        setModalOpen(true);
      } else {
        setModalContent({
          title: 'Error',
          message: 'Error adding course: ' + data.message,
          onConfirm: () => setModalOpen(false),
        });
        setModalOpen(true);
      }
    })
    .catch((error) => {
      setModalContent({
        title: 'Error',
        message: 'An unexpected error occurred. Please try again.',
        onConfirm: () => setModalOpen(false),
      });
      setModalOpen(true);
      console.error('Error:', error);
    });
};

  // this function handles changing the par value for a hole in the new course
  const handleHoleParChange = (index, value) => {
    const updatedHoles = [...newCourseHoles];
    updatedHoles[index] = { hole_number: index + 1, par: Number(value) };
    setNewCourseHoles(updatedHoles);

    const totalPar = updatedHoles.reduce((sum, hole) => sum + hole.par, 0);
    setNewCourseTotalPar(totalPar);
  };

  const handleParBlur = (index, value) => {
    // Remove leading zeros
    let cleaned = value.replace(/^0+/, '');
    // If empty after removing zeros, set to 0 (or you can set to 1 if you want a minimum)
    if (cleaned === '') cleaned = '0';

    const updatedHoles = [...newCourseHoles];
    updatedHoles[index] = { ...updatedHoles[index], par: Number(cleaned) };
    setNewCourseHoles(updatedHoles);

    const totalPar = updatedHoles.reduce((sum, hole) => sum + (Number(hole.par) || 0), 0);
    setNewCourseTotalPar(totalPar);
  };

  return (
    <div id="new-crazy-cup-page" className={`page ${fadeIn ? 'fade-in' : ''}`}>
      <h1 className="page-title">CREATE NEW&nbsp;EVENT</h1>
      {loading ? (
        <CreateEventSkeletonLoader />
      ) : (
        <div className="form-container">
          {/* Event Name Section */}
          <div className="form-group">
            <label htmlFor="crazy-cup-name" className="form-label">Event Name</label>
            <input
              type="text"
              id="crazy-cup-name"
              className="form-input"
              placeholder="Enter Crazy Cup Name"
              value={cupName}
              onChange={(e) => setCupName(e.target.value)}
              required
            />
          </div>
  
          {/* Course Selection Section */}
          <div className="form-group">
            <label htmlFor="golf-course" className="form-label">Select Courses</label>
            <div className="course-selection">
              <select
                id="golf-course"
                className="form-select"
                value={selectedCourse ? JSON.stringify(selectedCourse) : ''}
                onChange={(e) => {
                  if (e.target.value === 'add-new-course') {
                    setShowModal(true);
                  } else {
                    try {
                      const parsedCourse = JSON.parse(e.target.value);
                      setSelectedCourse(parsedCourse);
                    } catch (error) {
                      console.error('Error parsing selected course:', error);
                    }
                  }
                }}
              >
                <option value="" disabled>Courses</option>
                <option value="add-new-course">Add New Course</option>
                {golfCourses.map((course, index) => (
                  <option key={index} value={JSON.stringify(course)}>
                    {course.name}
                  </option>
                ))}
              </select>
              <button id="add-course" className="form-button" onClick={handleAddCourse}>
                Add Course
              </button>
            </div>
          </div>
  
          {/* Selected Courses Section */}
          <div className="form-group">
            <label className="form-label">Selected Courses</label>
            {selectedCourses.length === 0 && (
              <p style={{ color: '#888', fontStyle: 'italic', margin: '8px 0' }}>
                No courses selected.
              </p>
            )}
            <ul className="course-list">
              {selectedCourses.map((course, index) => (
                <li key={index} className="course-list-item">
                  {course.name} (Par:&nbsp;{course.par})
                  <button
                    id="remove-course-btn"
                    className="remove-button"
                    onClick={() => handleRemoveCourse(course.name)}
                  >
                    X
                  </button>
                </li>
              ))}
            </ul>
          </div>
  
          {/* Date Selection Section */}
          <div className="form-group">
            <label htmlFor="cup-date" className="form-label">Event Date</label>
            <input
              type="date"
              id="cup-date"
              className="form-input"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              required
            />
          </div>
  
          {/* Total Par and Action Buttons */}
          <div className="form-group">
            <h2 className="total-par">Total Par: {totalPar}</h2>
          </div>
          <div className="button-group">
              <button
                id="create-crazy-cup"
                className="main-button"
                onClick={handleCreateCup}
              >
                Create Crazy Cup
              </button>
              <button
                id="back-to-main"
                className="secondary-button"
                onClick={() => navigate('/')}
              >
                Back
              </button>
            </div>
        </div>
      )}
  
      {/* Add Course Modal */}
      {showModal && (
        <div id="add-course-modal-overlay">
          <div id="add-course-modal">
            <div id="add-course-modal-content">
              <h2>Add New Course</h2>
              <input
                type="text"
                placeholder="Course Name"
                value={newCourseName}
                onChange={(e) => setNewCourseName(e.target.value)}
                required
              />
              <select
                id="hole-count"
                value={newCourseHoles.length}
                onChange={(e) => {
                  const holeCount = Number(e.target.value);
                  const updatedHoles = Array(holeCount).fill({ par: 0 });
                  setNewCourseHoles(updatedHoles);
                  setNewCourseTotalPar(0);
                }}
              >
                <option value="9">9 Holes</option>
                <option value="18">18 Holes</option>
              </select>
              <div id="new-course-grid">
                <div className="grid-header">Hole</div>
                <div className="grid-header">Par</div>
                {newCourseHoles.map((hole, index) => (
                  <React.Fragment key={index}>
                    <div className="grid-item">{index + 1}</div>
                    <div className="grid-item">
                      <input
                        type="number"
                        inputMode="numeric"
                        min="1"
                        max="5"
                        value={hole.par === 0 ? '' : hole.par}
                        onChange={e => handleHoleParChange(index, e.target.value)}
                        onBlur={e => handleParBlur(index, e.target.value)}
                        style={{ fontSize: '1.1rem' }}
                      />
                    </div>
                  </React.Fragment>
                ))}
              </div>
              <h3>Total Par: {newCourseTotalPar}</h3>
              <button id="add-course-btn" className="button" onClick={handleAddNewCourse}>
                Add Course
              </button>
              <button className="button" onClick={() => setShowModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for displaying messages */}
      <CustomModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalContent.title}
        message={modalContent.message}
        onConfirm={modalContent.onConfirm}
      />

    </div>

    
  );
}

export default CreateNewCup;
